import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RouteBlock } from "@/components/RouteBlock";
import { toast } from "sonner";
import { Sparkles, Send, X, Loader2 } from "lucide-react";

export const ConciergeWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "concierge", text: "Hungry? Tell me your cravings — even a few at once — and I'll plan a food-truck crawl with a route. 🌮🍔🍦" },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [truckMap, setTruckMap] = useState({});
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/trucks").then((r) => { const m = {}; r.data.forEach((t) => (m[t.id] = t)); setTruckMap(m); }).catch(() => {});
  }, []);
  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading, open]);

  const ask = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "customer", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/concierge", { message: q, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "concierge", text: data.reply, truck_ids: data.truck_ids || [], route: data.route || [], maps_url: data.maps_url, maps_url_apple: data.maps_url_apple }]);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <>
      <button onClick={() => setOpen((v) => !v)} className="fixed bottom-6 right-6 z-[1000] w-14 h-14 rounded-full bg-[#0FB5A6] hover:bg-[#0E9E92] text-white gr-shadow-lg flex items-center justify-center active:scale-95 transition-transform" data-testid="concierge-fab" aria-label="Ask AI concierge">
        {open ? <X className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
      </button>
      {open && (
        <div className="fixed bottom-24 right-6 z-[1000] w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-2xl border border-teal-100 shadow-2xl flex flex-col overflow-hidden" data-testid="concierge-widget">
          <div className="px-4 py-3 bg-[#0FB5A6] text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5" /> <p className="font-display font-bold">FlavorFlow Concierge</p>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "customer" ? "justify-end" : "justify-start"}`}>
                <div className={m.role === "customer" ? "max-w-[85%]" : "w-full"}>
                  <div className={`rounded-2xl px-3 py-2 text-sm leading-relaxed ${m.role === "customer" ? "bg-[#0FB5A6] text-white" : "bg-teal-50 text-slate-800"}`} data-testid={`widget-msg-${i}`}>{m.text}</div>
                  {m.truck_ids?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {m.truck_ids.map((id) => truckMap[id] && <a key={id} href={`/truck/${id}`} className="px-2.5 py-1 rounded-full bg-white border border-teal-200 text-[#0FB5A6] text-xs font-bold hover:bg-teal-50">{truckMap[id].name}</a>)}
                    </div>
                  )}
                  {m.route?.length >= 2 && <RouteBlock route={m.route} mapsUrl={m.maps_url} appleUrl={m.maps_url_apple} />}
                </div>
              </div>
            ))}
            {loading && <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Planning your crawl…</div>}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="p-3 border-t border-teal-100 flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="e.g. tacos + dessert in Green Bay" className="rounded-full" data-testid="widget-input" />
            <Button type="submit" disabled={loading} className="rounded-full w-10 h-10 p-0 bg-[#0FB5A6] hover:bg-[#0E9E92] text-white shrink-0" data-testid="widget-send"><Send className="w-4 h-4" /></Button>
          </form>
        </div>
      )}
    </>
  );
};
